import { useCallback, useEffect, useMemo, useState } from 'react';
import { filterByWord } from '@/utils/global';
import GroupedTagSelectorDropDownContainer from './GroupedTagSelectorDropdown.container';
import TagSelectorDropDownComponent, {
  TagSelectorOption,
  TagSelectorDropDownProps,
} from './TagSelectorDropDown.component';

export type TagSelectorType = Pick<
  TagSelectorDropDownProps<TagSelectorOption | string>,
  | 'name'
  | 'label'
  | 'selectorLimit'
  | 'isLabelAboveField'
  | 'errorMessage'
  | 'onChangeDropDown'
  | 'disableDeleteAll'
  | 'isDisabled'
> & {
  options?: TagSelectorOption[] | string[];
  optionsWithLabel?: TagSelectorOption[];
  defaultValue?: string[];
};

const TagSelectorDropDownContainer = ({
  name,
  label,
  options,
  selectorLimit,
  isLabelAboveField,
  isDisabled,
  errorMessage,
  onChangeDropDown,
  disableDeleteAll,
  optionsWithLabel,
  defaultValue,
  ...props
}: TagSelectorType): JSX.Element => {
  const [isTagSelectorOpened, setIsTagSelectorOpened] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>(defaultValue || []);
  const [tagSelectorPlaceholder, setTagSelectorPlaceholder] = useState(
    `Choose up to ${selectorLimit}`
  );

  useEffect(() => {
    setSelectedOptions(defaultValue || []);
  }, [defaultValue]);

  const dropdownOptions = useMemo(() => {
    return optionsWithLabel ?? options ?? [];
  }, [options, optionsWithLabel]);

  const clearInput = (): void => {
    setInputValue('');
  };

  const formattedName = useMemo(() => {
    return (name ?? '').replace(/\s/g, '');
  }, [name]);

  const toggleTagSelectorHandler = (): void => {
    setIsTagSelectorOpened(!isTagSelectorOpened);
    clearInput();
  };

  const onRemoveItem = (index: number): void => {
    const doesIndexMatch = (option, optionIndex): boolean => optionIndex !== index;
    const filteredOptions = selectedOptions.filter(doesIndexMatch);

    setSelectedOptions(filteredOptions);

    onChangeDropDown?.({
      target: { value: filteredOptions, id: name, name },
      type: 'change',
    });
  };

  const removeAllOptionsHandler = (): void => {
    onChangeDropDown?.({
      target: { value: [], id: name, name },
      type: 'change',
    });
    setSelectedOptions([]);
    clearInput();
  };

  const selectedOptionsHandler = (option: string): void => {
    if (selectedOptions.includes(option)) {
      return;
    }

    onChangeDropDown?.({
      target: { value: [...selectedOptions, option], id: name, name },
      type: 'change',
    });

    setSelectedOptions([...selectedOptions, option]);
    setIsTagSelectorOpened(!isTagSelectorOpened);
    clearInput();
  };

  const onChangeDropDownHandler = useCallback(
    (event: any): void => {
      if (!isTagSelectorOpened) {
        setIsTagSelectorOpened(true);
      }

      setInputValue(event.target.value);
    },
    [isTagSelectorOpened]
  );

  const filteredOptions = useMemo(() => {
    if (!inputValue) {
      return dropdownOptions;
    }

    return (dropdownOptions as any[])
      .filter((option) => {
        const optionLabel = option?.label ?? option;
        return filterByWord(optionLabel, inputValue);
      })
      .filter((option) => {
        const optionValue = option?.value ?? option;
        return !selectedOptions.includes(optionValue);
      });
  }, [inputValue, dropdownOptions, selectedOptions]);

  useEffect(() => {
    if (isTagSelectorOpened) {
      setTagSelectorPlaceholder('Search for an option...');
      return;
    }

    setTagSelectorPlaceholder(`Choose up to ${selectorLimit}`);
  }, [isTagSelectorOpened, selectorLimit]);

  useEffect(() => {
    setSelectedOptions((prevOptions) => {
      if (selectorLimit) return prevOptions.filter((_, index) => index < selectorLimit);

      return prevOptions;
    });
  }, [selectorLimit]);

  const onPageClick = useCallback((event): void => {
    const tagSelectorId = 'tag-selector';
    const isTagSelector = (element): boolean => element?.id === tagSelectorId;
    const eventPath = event.composedPath() ?? [];
    const isClickingInsideTagSelector = eventPath.some(isTagSelector);

    if (!isClickingInsideTagSelector) {
      setIsTagSelectorOpened(false);
    }
  }, []);

  useEffect(() => {
    if (!isTagSelectorOpened) {
      return () => {};
    }

    window.addEventListener('click', onPageClick);
    return () => window.removeEventListener('click', onPageClick);
  }, [onPageClick, isTagSelectorOpened]);

  return (
    <TagSelectorDropDownComponent
      isDisabled={isDisabled}
      tagSelectorPlaceholder={tagSelectorPlaceholder}
      errorMessage={errorMessage}
      name={formattedName}
      label={label}
      disableDeleteAll={disableDeleteAll}
      selectorLimit={selectorLimit}
      inputValue={inputValue}
      filteredOptions={filteredOptions}
      allOptions={dropdownOptions}
      selectedOptions={selectedOptions}
      isLabelAboveField={isLabelAboveField}
      isTagSelectorOpened={isTagSelectorOpened}
      isOptionsWithLabel={!!optionsWithLabel}
      onChangeDropDown={onChangeDropDownHandler}
      selectedOptionsHandler={selectedOptionsHandler}
      toggleTagSelectorHandler={toggleTagSelectorHandler}
      onRemoveItem={onRemoveItem}
      removeAllOptionsHandler={removeAllOptionsHandler}
      {...props}
    />
  );
};

TagSelectorDropDownContainer.Grouped = GroupedTagSelectorDropDownContainer;

export default TagSelectorDropDownContainer;
